import { withMicrositeProps } from '@dustin-web/microsite-nextjs/src/with-server-data/with-microsite-props'
import { withMicrositeWrapper } from '@dustin-web/microsite-nextjs/src/with-server-data/with-microsite-wrapper'
import { InferGetServerSidePropsType } from 'next'
import {
  GetCategoryForFilterPageQuery,
  GetCategoryForFilterPageQueryVariables,
} from 'src/graphql/generated/graphql-types'
import { GetCategoryForFilterPage } from 'src/graphql/get-category-for-filter-page.graphql'
import {
  FacetParameterInput,
  parseParams,
} from '@dustin-web/microsite-components/src/utils/querystring'
import { constants } from '@dustin-web/microsite-config'
import { buildQueryState } from '@dustin-web/microsite-components/src/utils/querystate'
import FilterPageLayout, { type FilterPageMetaData, FilterPageType } from 'src/components/filters/filter-page-layout'
import { getCategoryFromPath, normalizePath } from 'src/lib/get-category-from-path'
import { getInfiniteCacheKeyFromDocument } from '@dustin-web/microsite-data/src/hooks/use-graphql-infinite'
import { infinityScrollPageSize } from 'src/components/constants'
import { useI18n } from '@dustin-web/microsite-components/src/i18n'
import { getSortBy } from 'src/lib/convert'

type Props = InferGetServerSidePropsType<typeof getServerSideProps>

const NewProductsPage = ({ categoryId }: Props) => {
  const { translate } = useI18n()

  const metaData: FilterPageMetaData = {
    type: FilterPageType.NewProducts,
    urlPartial: translate('New_Products_URL'),
    description: translate('New_Products_Metadescription'),
    seoText: translate('New_Products_IntroText'),
    title: translate('Header_News'),
    facetsFilterExpression: 'isnewproduct',
    digitalDataOrigin: 'newProductsPage',
    showOnlyUrlQuery: 'isnewproduct'
  }

  return (
    <FilterPageLayout
      categoryId={categoryId}
      pageMetaData={metaData}
      predefinedFacets={newProductsOnlyFacets}
    />
  )
}

export const getServerSideProps = withMicrositeProps(async ctx => {
  const { query, micrositeProps } = ctx
  const { cookies, fetchGraphql, requestContext, isToggleEnabled } = micrositeProps
  const { [constants.cookies.userSettings.sortBy]: sortBy } = cookies
  const parsedParams = parseParams(query)
  const queryState = buildQueryState(parsedParams)
  const { activeFilters: facets, page, searchwithin } = queryState
  const { log } = await import('@dustin-web/microsite-config/src/logger')

  const slugPath = Array.isArray(query.path) ? normalizePath(query.path.join('/')) : ''

  let props = {
    categoryId: HARDWARE_CATEGORY_ID,
    fallback: {},
  }

  if (slugPath) {
    const category = await getCategoryFromPath(
      slugPath,
      requestContext,
      isToggleEnabled('use-request-logging')
    )

    if (category) {
      props.categoryId = category.categoryId
    }
  }

  try {
    const combinedFacets = facets.concat(newProductsOnlyFacets)

    const variables = {
      categoryId: props.categoryId,
      facets: combinedFacets,
      page: page,
      pageSize: infinityScrollPageSize,
      searchPhrase: searchwithin,
      sortBy: getSortBy(sortBy),
    }

    const [_filterKey, filterData] = await fetchGraphql<
      GetCategoryForFilterPageQuery,
      GetCategoryForFilterPageQueryVariables
    >(GetCategoryForFilterPage, {
      variables,
    })

    props.fallback = {
      [getInfiniteCacheKeyFromDocument(GetCategoryForFilterPage, variables)]: [filterData],
    }

    return { props }
  } catch (err) {
    log.error(
      { err, traceIdentifier: requestContext.correlationId },
      'Error when loading data on new-products page'
    )
    props.fallback = {}

    return { notFound: true, props }
  }
})

export const newProductsOnlyFacets = [
  { name: 'showonly', values: ['isnewproduct'] },
  { name: 'isnewproduct', values: ['true'] },
] as FacetParameterInput[]

const HARDWARE_CATEGORY_ID = '2037738'

export default withMicrositeWrapper(NewProductsPage)
